import { encrypt } from "../utils/crypt.js";
import { log } from "../utils/logger.js";

function escapeBadChars(encryptedStr) {
    if (!encryptedStr) return encryptedStr;
    
    // Escape ALL non-alphanumeric characters except safe ones
    // Only allow: letters (a-z, A-Z), numbers (0-9), and safe chars (. _ -)
    return encryptedStr.replace(/[^a-zA-Z0-9._-]/g, match => {
        return `&#${match.charCodeAt(0)};`;
    });
  }

/**
 * Sends a request to deploy a chat widget
 * 
 * @param {Object} widgetConfig - Configuration for the widget deployment
 * @param {string} widgetConfig.userEmail - User email
 * @param {string} widgetConfig.title - Widget title
 * @param {string} widgetConfig.description - Widget description
 * @param {string} widgetConfig.processedFlow - Flow name for the deployment
 * @param {string} widgetConfig.primaryColor - Primary theme color
 * @param {string} widgetConfig.headerColor - Header background color
 * @param {string} widgetConfig.userBubbleColor - User message bubble color
 * @param {string} widgetConfig.assistantBubbleColor - Assistant message bubble color
 * @param {string} widgetConfig.backgroundGradientFrom - Background gradient start color
 * @param {string} widgetConfig.backgroundGradientTo - Background gradient end color
 * @param {string} widgetConfig.headerTextColor - Text color for header
 * @param {string} widgetConfig.userTextColor - Text color for user messages
 * @param {string} widgetConfig.assistantTextColor - Text color for assistant messages
 * @param {string} widgetConfig.buttonHoverColor - Hover color for buttons
 * @param {string} widgetConfig.buttonIconColor - Icon color for floating chat button
 * @param {string} widgetConfig.headerIcon - URL for header icon
 * @param {string} widgetConfig.headerTitle - Title displayed in widget header
 * @param {string} widgetConfig.initialMessage - Initial greeting message
 * @param {string} widgetConfig.stockReply - Fallback reply message
 * @param {string} widgetConfig.popupMessage - Message to show in popup (optional)
 * @param {number} widgetConfig.popupTimer - Time in ms before showing popup
 * @returns {Promise<Object>} Deployment response with domain information
 */
async function deployWidgetRequest(widgetConfig) {
    try {
        // Extract required parameters
        const { 
            userEmail, 
            title, 
            description, 
            processedFlow,  // This should be the flow name already processed
            primaryColor,
            headerColor,
            userBubbleColor,
            assistantBubbleColor,
            backgroundGradientFrom,
            backgroundGradientTo,
            headerTextColor,
            userTextColor,
            assistantTextColor,
            buttonHoverColor,
            buttonIconColor,
            headerIcon,
            headerTitle,
            initialMessage,
            stockReply,
            popupMessage,
            popupTimer
        } = widgetConfig;
        
        // Create deployment ID in the required format (just add widget: prefix)
        const deploymentId = `widget:${title}`;
        
        const API_URL = 'https://api.vectorvault.io/';
        const userEncrypted = escapeBadChars(encrypt(userEmail))
        
        // Construct the request payload with all widget configuration data
        const payload = {
            email: userEncrypted,
            deployment_id: deploymentId,
            flow_name: processedFlow,  // Using the processed flow name directly
            
            // Widget metadata
            title: title,
            description: description,
            
            // Appearance settings
            primary_color: primaryColor,
            header_color: headerColor,
            user_bubble_color: userBubbleColor,
            assistant_bubble_color: assistantBubbleColor,
            background_gradient_from: backgroundGradientFrom,
            background_gradient_to: backgroundGradientTo,
            
            // Text colors
            header_text_color: headerTextColor,
            user_text_color: userTextColor,
            assistant_text_color: assistantTextColor,
            
            // Button colors
            button_hover_color: buttonHoverColor,
            button_icon_color: buttonIconColor,
            
            // Header settings
            header_icon: headerIcon,
            header_title: headerTitle,
            
            // Message settings
            initial_message: initialMessage,
            stock_reply: stockReply,
            
            // Popup settings
            popup_message: popupMessage,
            popup_timer: popupTimer
        };
        
        // Log the request payload for debugging
        log('Widget deployment request payload:', payload);
        
        // Send the POST request to the API
        const response = await fetch(`${API_URL}/widget-deployment`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to deploy widget: ${errorText}`);
        }
        
        // Parse and return the response
        const data = await response.json();
        log('Widget deployment response:', data);
        
        return data;
    } catch (error) {
        console.error('Error in widget deployment request:', error);
        throw error;
    }
}
export default deployWidgetRequest;